import { supabase } from "../../../foundation/altzero_base/base/utils/supabaseClient";
import { normalizeEnvironmentalCharges } from '../lib/normalizeCharges';
import { sequenceEnvCharges } from '../lib/envYearSequence';
import { setTermYearMapping, setActiveTender, mergeTermYearMappings } from '../lib/termLabel';
import type { RankedOfferSelection } from '../types/offerTypes';

/**
 * Get current user's organization ID
 */
export const getUserOrganizationId = async (): Promise<string | null> => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user?.id) {
      console.error("❌ No user session found");
      return null;
    }

    const { data: orgMember, error } = await supabase
      .from('base_organisation_members')
      .select('organisation_id')
      .eq('user_id', session.user.id)
      .limit(1)
      .single();

    if (error) {
      console.error("❌ Error getting organization ID:", error);
      return null;
    }

    return orgMember?.organisation_id || null;
  } catch (error) {
    console.error("❌ Unexpected error getting organization ID:", error);
    return null;
  }
};

/**
 * Create or get tender for a client
 */
export const createOrGetTender = async (data: {
  orgId: string;
  clientId: string;
  title: string;
  description?: string;
}): Promise<{ success: boolean; tenderId?: string; error?: string }> => {
  try {
    // Check if tender already exists for this client
    const { data: existingTender, error: fetchError } = await supabase
      .from("tender")
      .select("id")
      .eq("org_id", data.orgId)
      .eq("client_id", data.clientId)
      .eq("status", "draft")
      .order("created_at", { ascending: false })
      .limit(1)
      .single();

    if (existingTender && !fetchError) {
      console.log("✅ Found existing tender:", existingTender.id);
      return { success: true, tenderId: existingTender.id };
    }

    // Create new tender
    const { data: newTender, error: createError } = await supabase
      .from("tender")
      .insert({
        org_id: data.orgId,
        client_id: data.clientId,
        opportunity_name: data.title,
        account_name: data.title,
        status: "draft",
        created_at: new Date().toISOString(),
      })
      .select("id")
      .single();

    if (createError) {
      console.error("❌ Error creating tender:", createError);
      return { success: false, error: createError.message };
    }

    console.log("✅ Created new tender:", newTender.id);
    return { success: true, tenderId: newTender.id };
  } catch (error) {
    console.error("❌ Unexpected error in createOrGetTender:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error",
    };
  }
};

/**
 * Save sites to database
 */
export const saveSites = async (data: {
  orgId: string;
  clientId: string;
  sites: any[];
}): Promise<{ success: boolean; savedSites?: any[]; count?: number; error?: string }> => {
  try {
    console.log(`\n${"=".repeat(80)}`);
    console.log(`🏢 SAVING SITES TO DATABASE`);
    console.log(`${"=".repeat(80)}`);
    console.log(`📊 Summary:`);
    console.log(`   - Number of sites: ${data.sites.length}`);
    console.log(`   - Client ID: ${data.clientId}`);
    console.log(`   - Org ID: ${data.orgId}`);
    console.log(`\n📦 RAW SITES DATA (from frontend):`);
    console.log(JSON.stringify(data.sites, null, 2));

    const sitesToInsert = data.sites.map((site, index) => {
      const transformedSite = {
        org_id: data.orgId,
        client_id: data.clientId,
        name: site.name || "",
        nmi: site.nmi || "",
        abn: site.abn || "",
        acn: site.acn || "",
        due_at: site.dueAt || null,
        current_contract_expiry: site.currentContractExpiry ? new Date(site.currentContractExpiry).toISOString().split('T')[0] : null,
        current_meter_provider: site.currentMeterProvider || "",
        current_retailer: site.currentRetailer || "",
        dma_provider: site.dmaProvider || "",
        retailer_comms: site.retailerComms || "",
        create_dma: site.createDma || false,
        request_meter_data: site.requestMeterData || false,
        request_bills: site.requestBills || false,
        acquire_ced: site.acquireCed || false,
        green_power: site.greenPower || false,
        annual_kwh_peak: site.annualKwhPeak || null,
        annual_kwh_offpeak: site.annualKwhOffpeak || null,
        annual_kwh_shoulder: site.annualKwhShoulder || null,
        annual_kwh_total: site.annualKwhTotal || (() => {
          const peak = site.annualKwhPeak || 0;
          const offpeak = site.annualKwhOffpeak || 0;
          const shoulder = site.annualKwhShoulder || 0;
          const total = peak + offpeak + shoulder;
          return total > 0 ? total : null;
        })(),
        daily_charges: site.dailyCharges || null,
        non_energy_charges: site.nonEnergyCharges || null,
        peak_demand_kw: site.peakDemandKw || 0,
        current_contract_rates: site.currentContractRates || {},
        document_file_paths: site.documentFilePaths || [],
        retailer_notes: site.retailerNotes || null,
        addr_street: site.address?.street || "",
        addr_city: site.address?.city || "",
        addr_state: site.address?.state || "",
        addr_postcode: site.address?.postcode || "",
        addr_country: site.address?.country || "Australia",
        extn: site.energyDetails || {},
        created_at: new Date().toISOString(),
      };

      console.log(`\n🔄 SITE #${index + 1} - TRANSFORMED FOR DATABASE:`);
      console.log(`   Name: ${transformedSite.name}`);
      console.log(`   NMI: ${transformedSite.nmi}`);
      console.log(`   Address: ${transformedSite.addr_street}, ${transformedSite.addr_city}, ${transformedSite.addr_state}`);
      console.log(`\n   📋 Full Payload:`);
      console.log(JSON.stringify(transformedSite, null, 2));

      return transformedSite;
    });

    console.log(`\n${"=".repeat(80)}`);
    console.log(`📤 FINAL PAYLOAD TO SUPABASE (${sitesToInsert.length} sites):`);
    console.log(`${"=".repeat(80)}`);
    console.log(JSON.stringify(sitesToInsert, null, 2));
    console.log(`${"=".repeat(80)}\n`);

    // Use upsert to prevent duplicates - conflict on client_id + nmi combination
    // If a site with same client_id and nmi exists, update it instead of creating new
    const { data: savedSites, error } = await supabase
      .from("site")
      .upsert(sitesToInsert, {
        onConflict: 'client_id,nmi',
        ignoreDuplicates: false, // Update existing records
      })
      .select();

    if (error) {
      console.error("❌ Error saving sites:", error);
      // If upsert fails due to missing unique constraint, fall back to checking existing
      if (error.code === '42P10' || error.message.includes('unique constraint')) {
        console.log("⚠️ Upsert not available, checking for existing sites...");
        // Check which sites already exist by NMI
        const nmis = sitesToInsert.map(s => s.nmi).filter(Boolean);
        const { data: existingSites } = await supabase
          .from("site")
          .select("*")
          .eq("client_id", data.clientId)
          .in("nmi", nmis);

        if (existingSites && existingSites.length > 0) {
          console.log(`✅ Found ${existingSites.length} existing sites, skipping insert`);
          return { success: true, savedSites: existingSites, count: existingSites.length };
        }
        // No existing sites, try regular insert
        const { data: insertedSites, error: insertError } = await supabase
          .from("site")
          .insert(sitesToInsert)
          .select();
        if (insertError) {
          return { success: false, error: insertError.message };
        }
        return { success: true, savedSites: insertedSites, count: insertedSites?.length || 0 };
      }
      return { success: false, error: error.message };
    }

    console.log(`✅ Successfully saved/updated ${savedSites.length} sites`);
    return { success: true, savedSites, count: savedSites.length };
  } catch (error) {
    console.error("❌ Unexpected error in saveSites:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error",
    };
  }
};

/**
 * Create tender-site junction records
 * @param data.sites - Sites with database IDs (after saving)
 * @param data.originalSiteIds - Optional map of database site.id -> original frontend site.id
 */
export const saveTenderSites = async (data: {
  orgId: string;
  tenderId: string;
  sites: any[];
  originalSiteIds?: Map<string, string>; // Map of dbSiteId -> frontendSiteId
}): Promise<{ success: boolean; tenderSites?: any[]; siteIdMap?: Map<string, string>; error?: string }> => {
  try {
    console.log(`\n${"=".repeat(80)}`);
    console.log(`🔗 CREATING TENDER-SITE JUNCTION RECORDS`);
    console.log(`${"=".repeat(80)}`);

    // Check which sites exist in the database
    const siteIds = data.sites.map(s => s.id).filter(Boolean);
    let existingSites: any[] = [];
    
    if (siteIds.length > 0) {
      const { data: dbSites, error: checkError } = await supabase
        .from("site")
        .select("id")
        .in("id", siteIds);
      
      if (!checkError && dbSites) {
        existingSites = dbSites;
      }
    }

    const existingSiteIds = new Set(existingSites.map(s => s.id));
    
    // Create tender_site records, setting site_id to null if site doesn't exist in DB
    // (site_id is nullable in the schema, so this is allowed)
    const tenderSitesToInsert = data.sites.map((site) => {
      // Only set site_id if it exists in the database
      const dbSiteId = existingSiteIds.has(site.id) ? site.id : null;
      
      return {
        org_id: data.orgId,
        tender_id: data.tenderId,
        site_id: dbSiteId, // null if site doesn't exist in DB
        name: site.name,
        nmi: site.nmi,
        addr_street: site.address?.street || '',
        addr_city: site.address?.city || '',
        addr_state: site.address?.state || '',
        addr_postcode: site.address?.postcode || '',
        addr_country: site.address?.country || 'Australia',
        annual_kwh_peak: site.annualKwhPeak || null,
        annual_kwh_offpeak: site.annualKwhOffpeak || null,
        annual_kwh_shoulder: site.annualKwhShoulder || null,
        daily_charges: site.dailyCharges || 0,
        non_energy_charges: site.nonEnergyCharges || null,
        peak_demand_kw: site.peakDemandKw || 0,
        current_contract_rates: site.currentContractRates || {},
        retailer_notes: site.retailerNotes || null,
        extn: {},
        created_at: new Date().toISOString(),
      };
    });

    // Log warning if any sites don't exist in DB
    const missingSites = data.sites.filter(s => s.id && !existingSiteIds.has(s.id));
    if (missingSites.length > 0) {
      console.warn(`⚠️ Warning: ${missingSites.length} site(s) not found in database. Creating tender_site records with site_id=null:`,
        missingSites.map(s => s.name || s.id));
    }

    // Check for existing tender_site records to avoid duplicates
    const nmis = tenderSitesToInsert.map(ts => ts.nmi).filter(Boolean);
    const { data: existingTenderSites } = await supabase
      .from("tender_site")
      .select("*")
      .eq("tender_id", data.tenderId)
      .in("nmi", nmis);

    if (existingTenderSites && existingTenderSites.length > 0) {
      console.log(`✅ Found ${existingTenderSites.length} existing tender_site records, reusing them`);

      // Create a map of frontend site.id -> tender_site.id from existing records
      const siteIdMap = new Map<string, string>();
      existingTenderSites.forEach((ts: any) => {
        // Find the matching site by NMI
        const matchingSite = data.sites.find(s => s.nmi === ts.nmi);
        if (matchingSite && matchingSite.id) {
          const frontendSiteId = data.originalSiteIds?.get(matchingSite.id) || matchingSite.id;
          siteIdMap.set(frontendSiteId, ts.id);
          console.log(`📌 Reusing: frontend site.id "${frontendSiteId}" -> tender_site.id "${ts.id}"`);
        }
      });

      // Check if we need to insert any new tender_sites (for sites not in existing)
      const existingNmis = new Set(existingTenderSites.map((ts: any) => ts.nmi));
      const newTenderSites = tenderSitesToInsert.filter(ts => !existingNmis.has(ts.nmi));

      if (newTenderSites.length > 0) {
        console.log(`📦 Inserting ${newTenderSites.length} new tender_site records...`);
        const { data: newSaved, error: newError } = await supabase
          .from("tender_site")
          .insert(newTenderSites)
          .select();

        if (newError) {
          console.error("❌ Error creating new tender-site records:", newError);
        } else if (newSaved) {
          newSaved.forEach((ts: any, index: number) => {
            const matchingSite = data.sites.find(s => s.nmi === ts.nmi);
            if (matchingSite && matchingSite.id) {
              const frontendSiteId = data.originalSiteIds?.get(matchingSite.id) || matchingSite.id;
              siteIdMap.set(frontendSiteId, ts.id);
            }
          });
        }
      }

      console.log(`📋 SiteIdMap contains ${siteIdMap.size} entries`);
      return { success: true, tenderSites: existingTenderSites, siteIdMap };
    }

    // No existing tender_sites, insert all
    const { data: savedTenderSites, error } = await supabase
      .from("tender_site")
      .insert(tenderSitesToInsert)
      .select();

    if (error) {
      console.error("❌ Error creating tender-site records:", error);
      return { success: false, error: error.message };
    }

    // Create a map of frontend site.id -> tender_site.id
    // This is critical: offers use frontend site IDs, so we need to map those
    const siteIdMap = new Map<string, string>();
    savedTenderSites.forEach((ts: any, index: number) => {
      const dbSite = data.sites[index];
      if (dbSite && dbSite.id) {
        // If we have originalSiteIds mapping, use it to get the frontend ID
        // Otherwise, use the dbSite.id (assuming it's the same)
        const frontendSiteId = data.originalSiteIds?.get(dbSite.id) || dbSite.id;
        siteIdMap.set(frontendSiteId, ts.id);
        console.log(`📌 Mapped frontend site.id "${frontendSiteId}" (db: ${dbSite.id}) -> tender_site.id "${ts.id}"`);
      }
    });

    console.log(`✅ Successfully created ${savedTenderSites.length} tender-site records`);
    console.log(`📋 SiteIdMap contains ${siteIdMap.size} entries:`, Array.from(siteIdMap.entries()).map(([k, v]) => `${k} -> ${v}`));
    return { success: true, tenderSites: savedTenderSites, siteIdMap };
  } catch (error) {
    console.error("❌ Unexpected error in saveTenderSites:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error",
    };
  }
};

/**
 * Save offers to database
 */
export const saveOffers = async (data: {
  orgId: string;
  clientId: string;
  tenderId: string;
  offers: any[];
  siteIdMap?: Map<string, string>;  // NEW: Map of site.id -> tender_site.id
}): Promise<{ success: boolean; savedOffers?: any[]; count?: number; error?: string }> => {
  try {
    console.log(`\n${"=".repeat(80)}`);
    console.log(`💾 SAVING OFFERS TO DATABASE`);
    console.log(`${"=".repeat(80)}`);
    console.log(`📊 Summary:`);
    console.log(`   - Number of offers: ${data.offers.length}`);
    console.log(`   - Tender ID: ${data.tenderId}`);
    console.log(`   - Client ID: ${data.clientId}`);
    console.log(`   - Org ID: ${data.orgId}`);
    console.log(`\n📦 RAW OFFERS DATA (from frontend):`);
    console.log(JSON.stringify(data.offers, null, 2));

    // Validate that siteIdMap exists and has entries
    if (!data.siteIdMap || data.siteIdMap.size === 0) {
      return {
        success: false,
        error: "No tender_site records found. Please ensure sites are saved before creating offers.",
      };
    }

    // Check for offers with missing siteId or invalid tender_site_id
    const offersWithMissingSite = data.offers.filter(offer => {
      if (!offer.siteId) return true;
      const tenderSiteId = data.siteIdMap!.get(offer.siteId);
      return !tenderSiteId;
    });

    if (offersWithMissingSite.length > 0) {
      const missingSiteIds = offersWithMissingSite.map(o => o.siteId || 'missing').join(', ');
      return {
        success: false,
        error: `Cannot save offers: ${offersWithMissingSite.length} offer(s) have invalid or missing siteId. Missing siteIds: ${missingSiteIds}. Please ensure all offers are assigned to valid sites.`,
      };
    }

    const offersToInsert = data.offers.map((offer, index) => {
      // Get tender_site_id from the map - this should always exist after validation above
      const tenderSiteId = data.siteIdMap!.get(offer.siteId!);
      
      if (!tenderSiteId) {
        throw new Error(`Missing tender_site_id for offer ${offer.id} with siteId ${offer.siteId}`);
      }

      const transformedOffer = {
        id: offer.id,  // CRITICAL: Include the frontend-generated ID
        org_id: data.orgId,
        client_id: data.clientId,
        tender_id: data.tenderId,
        tender_site_id: tenderSiteId,  // NEW: Link to tender_site (REQUIRED - NOT NULL)

        // Basic fields (matching user's schema EXACTLY)
        retailer_id: offer.retailerId || null,
        offer_type: offer.offerType || "electricity",
        green_percent: offer.greenPercent || 0,

        // Multi-year rate data — normalize env charge key/field naming only (no `defaults` arg
        // passed here deliberately). This save path runs on EVERY save from Offers step,
        // including offers that already went through review with a deliberately-cleared rate —
        // re-deriving here on every save would silently overwrite that decision with a freshly
        // computed number each time. Same reasoning as OfferManagementCard/CompareAndSelectStep.
        multi_year_rate_data: (() => {
          const myr = offer.multiYearRateData;
          if (!myr) return null;

          const smooth = myr.smooth;
          const stepped = (myr as Record<string, unknown>).stepped as
            { environmentalCharges?: unknown } | undefined;

          /**
           * ⚠️ TWO DIFFERENT TREATMENTS, DELIBERATELY.
           *
           * `smooth` is fully normalised, as it always has been: key mapping, rate derivation,
           * and now the year sequencing that normaliseEnvironmentalCharges carries.
           *
           * `stepped` gets the SEQUENCING ONLY. Running the full normaliser over it would also
           * rewrite its key names and derive rates, and the stepped block is read raw in several
           * places — envChargesSource reads `stepped.environmental_charges`, and
           * parsedOfferValidation reads both spellings. Reshaping it on save would change what
           * those readers find, which is a far larger change than the overlap this is fixing.
           *
           * The sequencing alone touches nothing but `date_range`, so a stepped offer keeps the
           * exact shape it has today and simply stops double-counting an overlapping quarter.
           */
          const normalisedSmooth = smooth?.environmentalCharges
            ? {
                ...smooth,
                environmentalCharges:
                  normalizeEnvironmentalCharges(smooth.environmentalCharges as Record<string, unknown>)
                  || smooth.environmentalCharges,
              }
            : smooth;

          const sequencedStepped = stepped?.environmentalCharges
            ? {
                ...stepped,
                environmentalCharges: sequenceEnvCharges(
                  stepped.environmentalCharges as Record<string, unknown>,
                ),
              }
            : stepped;

          return {
            ...myr,
            ...(normalisedSmooth ? { smooth: normalisedSmooth } : {}),
            ...(sequencedStepped ? { stepped: sequencedStepped } : {}),
          };
        })(),

        // Dynamic contract terms and dates from parsed data
        available_terms: offer.availableTerms || null,
        expiry_date: offer.expiryDate ? new Date(offer.expiryDate).toISOString().split('T')[0] : null,
        validity_date: offer.validityDate ? new Date(offer.validityDate).toISOString().split('T')[0] : null,

        // Recommendation tracking
        is_recommended: offer.isRecommended || false,
        recommended_months: offer.recommendedMonths || [],

        // Everything else goes into extn jsonb field
        extn: (() => {
          // Normalize environmental charges to consistent format (uppercase keys, camelCase fields).
          // Priority flipped from extn-first to multiYearRateData-first: extn.environmentalCharges
          // is never touched by field edits (updateOfferRateData only writes multiYearRateData),
          // so once populated on the first save it stayed stale forever — every later save just
          // re-wrote that same stale copy back into the DB, so a deleted/edited rate never actually
          // reached here even though multiYearRateData itself was correct.
          const rawEnv = offer.multiYearRateData?.smooth?.environmentalCharges
            || (offer.feesJson as any)?.environmentalCharges
            || offer.extn?.environmentalCharges
            || null;
          // No `defaults` arg here either — same reasoning as multi_year_rate_data above.
          const normalizedEnv = rawEnv ? (normalizeEnvironmentalCharges(rawEnv as Record<string, unknown>) || rawEnv) : null;

          return {
            retailerName: offer.retailerName || "",
            retailerLogo: offer.retailerLogo || null,
            feesJson: offer.feesJson || {},
            // Mirror of the term -> year assignment, kept beside contract_terms the way
            // offerAutoPopulateService writes it. extn is built field-by-field here, so
            // anything not named is dropped; multi_year_rate_data above carries the
            // authoritative copy, and this keeps the two from disagreeing.
            term_year_labels: (offer as any).multiYearRateData?.term_year_labels
              ?? (offer as any).extn?.term_year_labels ?? null,
            status: offer.status || "draft",
            notes: offer.notes || null,
            estimatedAnnualCost: offer.estimatedAnnualCost || null,
            estimatedSavings: offer.estimatedSavings || null,
            estimatedSavingsPercent: offer.estimatedSavingsPercent || null,
            sourceFileId: offer.sourceFileId || null,
            sourceCitations: offer.sourceCitations || null,
            confidence: offer.confidence || null,
            rationale: offer.rationale || null,
            offerLines: offer.offerLines || [],
            // CRITICAL: Preserve contract_terms (per-term rate data) — used by calculation engine
            contract_terms: offer.extn?.contract_terms || null,
            parsedOfferId: offer.extn?.parsedOfferId || null,
            // User's explicit rate type selection (Smooth/Stepped) — overrides backend inference
            selectedRateType: offer.extn?.selectedRateType || null,
            // Normalized env charges — preferred source for calculation & display
            environmentalCharges: normalizedEnv,
          };
        })(),

        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      console.log(`\n🔄 OFFER #${index + 1} - TRANSFORMED FOR DATABASE:`);
      console.log(`   Retailer: ${transformedOffer.extn.retailerName || 'Not selected'}`);
      console.log(`   Type: ${transformedOffer.offer_type}`);
      console.log(`   Available Terms: [${(transformedOffer.available_terms || []).join(', ')}] months`);
      console.log(`   Green %: ${transformedOffer.green_percent}%`);
      console.log(`   Status: ${transformedOffer.extn.status}`);
      console.log(`   Recommended: ${transformedOffer.is_recommended}`);
      console.log(`   Recommended Months: [${transformedOffer.recommended_months.join(', ')}]`);
      console.log(`\n   🌿 Environmental Charges (normalized, in extn):`, JSON.stringify(transformedOffer.extn.environmentalCharges, null, 2));
      console.log(`\n   💰 Fees JSON (in extn):`);
      console.log(JSON.stringify(transformedOffer.extn.feesJson, null, 2));
      console.log(`\n   📊 Multi-Year Rate Data:`);
      console.log(JSON.stringify(transformedOffer.multi_year_rate_data, null, 2));
      console.log(`\n   📋 Full Payload:`);
      console.log(JSON.stringify(transformedOffer, null, 2));

      return transformedOffer;
    });

    console.log(`\n${"=".repeat(80)}`);
    console.log(`📤 FINAL PAYLOAD TO SUPABASE (${offersToInsert.length} offers):`);
    console.log(`${"=".repeat(80)}`);
    console.log(JSON.stringify(offersToInsert, null, 2));
    console.log(`${"=".repeat(80)}\n`);

    console.log(`\n🚀 EXECUTING UPSERT FOR ${offersToInsert.length} OFFERS...`);
    const { data: savedOffers, error } = await supabase
      .from("offer")
      .upsert(offersToInsert, { onConflict: 'id' })
      .select();

    if (error) {
      console.error("❌ ERROR SAVING OFFERS TO DATABASE:", error);
      console.error("Error details:", JSON.stringify(error, null, 2));
      return { success: false, error: error.message };
    }

    if (!savedOffers || savedOffers.length === 0) {
      console.error("❌ NO OFFERS RETURNED FROM DATABASE AFTER UPSERT");
      return { success: false, error: "No offers were saved to database" };
    }

    console.log(`✅ SUCCESSFULLY SAVED ${savedOffers.length} OFFERS TO DATABASE:`);
    savedOffers.forEach((offer, index) => {
      console.log(`   ${index + 1}. ${offer.id} (${offer.retailer_name}) - ${offer.created_at}`);
    });

    // Clean up duplicate offer rows for the same (tender_site_id, retailer_id) pair. Repeated
    // visits to the offer-review step can regenerate a fresh frontend id for an offer that
    // already has a real DB row — upserting that fresh id INSERTS a second row instead of
    // updating the first, and both then show up side-by-side in Compare & Select. This keeps
    // whichever row this call just saved (or the most recent one) and removes the rest.
    const savedIds = new Set(offersToInsert.map((o: any) => o.id));
    const { data: allTenderOffers } = await supabase
      .from("offer")
      .select("id, tender_site_id, retailer_id, created_at")
      .eq("tender_id", data.tenderId);

    if (allTenderOffers && allTenderOffers.length > 0) {
      const groups = new Map<string, typeof allTenderOffers>();
      allTenderOffers.forEach((o) => {
        if (!o.tender_site_id || !o.retailer_id) return; // ambiguous grouping key — leave alone
        const key = `${o.tender_site_id}::${o.retailer_id}`;
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key)!.push(o);
      });

      const idsToDelete: string[] = [];
      groups.forEach((group) => {
        if (group.length <= 1) return;
        const keep = group.find((o) => savedIds.has(o.id))
          || group.reduce((a, b) => (new Date(a.created_at) > new Date(b.created_at) ? a : b));
        group.forEach((o) => { if (o.id !== keep.id) idsToDelete.push(o.id); });
      });

      if (idsToDelete.length > 0) {
        console.log(`🧹 Removing ${idsToDelete.length} duplicate offer row(s):`, idsToDelete);
        await supabase.from("offer").delete().in("id", idsToDelete);
      }
    }

    return { success: true, savedOffers, count: savedOffers.length };
  } catch (error) {
    console.error("❌ Unexpected error in saveOffers:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error",
    };
  }
};

/**
 * Permanently delete a single offer row from the DB. Removing an offer in OffersStep only ever
 * updated local/context state — saveOffers upserts by id and never deletes rows missing from its
 * input, so a "removed" offer's DB row stayed untouched and kept reappearing in Compare & Select
 * (which reads offers straight from the DB). This performs the actual deletion immediately, so
 * removal is permanent everywhere right away instead of depending on a later bulk save.
 */
export const deleteOffer = async (offerId: string): Promise<{ success: boolean; error?: string }> => {
  try {
    const { error } = await supabase.from("offer").delete().eq("id", offerId);
    if (error) return { success: false, error: error.message };
    return { success: true };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error",
    };
  }
};

/**
 * ⚠️ REMOVING A SITE FROM THE FORM NEVER REACHED THE DATABASE.
 *
 * SitesManagementSection's remove button only filtered React state, and saveTenderSites inserts
 * or reuses by NMI — it has never deleted a tender_site row that the form no longer carries.
 * Nothing anywhere in the codebase deleted one. So the row survived, and every later step that
 * reads tender_site rather than the form — the calculation, the proposal's site list — still saw
 * the site the team had removed. That is the same shape of defect handleRemoveOffer was given
 * deleteOffer for; sites never got the equivalent.
 *
 * ⚠️ THIS CASCADES. offer.tender_site_id is ON DELETE CASCADE, and all 3,091 offers on prod
 * carry one, so removing a site takes that site's offers with it. That is what removing a site
 * MEANS — but it is why this is a targeted delete of one row the user explicitly asked to drop,
 * keyed on (tender_id, nmi), rather than a reconcile of "whatever is missing from the payload".
 * A reconcile would turn one partial save into the loss of a tender's offers.
 *
 * (tender_id, nmi) identifies exactly one row: checked on prod — 1,401 rows, no duplicate pair,
 * no blank NMI — and it is the same key saveTenderSites reuses rows by.
 *
 * A site the tender never saved has no row; deleting nothing is success, not an error.
 */
export const deleteTenderSite = async (
  tenderId: string,
  nmi: string,
): Promise<{ success: boolean; deleted: number; error?: string }> => {
  if (!tenderId || !nmi?.trim()) {
    // Nothing was ever persisted for this site — removing it from the form is the whole job.
    return { success: true, deleted: 0 };
  }
  try {
    const { data, error } = await supabase
      .from("tender_site")
      .delete()
      .eq("tender_id", tenderId)
      .eq("nmi", nmi.trim())
      .select("id");

    if (error) return { success: false, deleted: 0, error: error.message };
    return { success: true, deleted: data?.length ?? 0 };
  } catch (error) {
    return {
      success: false,
      deleted: 0,
      error: error instanceof Error ? error.message : "Unknown error",
    };
  }
};

/**
 * Get sites for a client
 */
export const getSites = async (clientId: string): Promise<{ success: boolean; sites?: any[]; error?: string }> => {
  try {
    const { data: sites, error } = await supabase
      .from("site")
      .select("*")
      .eq("client_id", clientId)
      .order("created_at", { ascending: false });

    if (error) {
      console.error("❌ Error fetching sites:", error);
      return { success: false, error: error.message };
    }

    return { success: true, sites: sites || [] };
  } catch (error) {
    console.error("❌ Unexpected error in getSites:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error",
    };
  }
};

/**
 * Get offers for a tender
 */
export const getOffers = async (tenderId: string): Promise<{ success: boolean; offers?: any[]; error?: string }> => {
  // Name the tender being rendered BEFORE any await, so a label never resolves against the
  // tender the user just left. Assignments are stored per tender (see termLabel.ts), so this
  // needs no clearing — an unseen tender simply has none yet and derives until its offers land.
  setActiveTender(tenderId);

  try {
    console.log(`🔍 FETCHING OFFERS FOR TENDER: ${tenderId}`);

    // First, fetch tender_site records to map tender_site_id -> site_id
    const { data: tenderSites, error: tenderSitesError } = await supabase
      .from("tender_site")
      .select("id, site_id")
      .eq("tender_id", tenderId);

    if (tenderSitesError) {
      console.error("❌ Error fetching tender_sites:", tenderSitesError);
      return { success: false, error: tenderSitesError.message };
    }

    // Create map: tender_site_id -> site_id (database)
    const tenderSiteToSiteIdMap = new Map<string, string>();
    tenderSites?.forEach(ts => {
      if (ts.id && ts.site_id) {
        tenderSiteToSiteIdMap.set(ts.id, ts.site_id);
      }
    });

    console.log(`📋 Created tender_site_id -> site_id map with ${tenderSiteToSiteIdMap.size} entries`);

    const { data: offers, error } = await supabase
      .from("offer")
      .select(`
        *,
        retailer:retailer_id (
          id,
          name,
          logo
        ),
        tender_site:tender_site_id (
          id,
          site_id,
          name
        )
      `)
      .eq("tender_id", tenderId)
      .order("created_at", { ascending: false });

    if (error) {
      console.error("❌ Error fetching offers:", error);
      return { success: false, error: error.message };
    }

    // Put the tender's term -> year assignment in force before anything renders a label.
    //
    // The reviewer sets it per offer in Step 3, but every screen from Compare & Select onward
    // ranks all retailers under ONE set of year columns, so the tender needs a single answer.
    // Roughly 21 components label terms from a bare `offer.termMonths` with no route to the
    // assignment; setting it here means they all agree without threading it through each one
    // (the same arrangement setTermBuckets uses — see termLabel.ts, which also explains why the
    // server must never rely on this).
    //
    // Ordered oldest-first so the merge is stable regardless of the descending fetch order.
    setTermYearMapping(
      mergeTermYearMappings(
        [...(offers || [])]
          .sort((a: any, b: any) => String(a.created_at).localeCompare(String(b.created_at)))
          .map((o: any) => o.multi_year_rate_data?.term_year_labels ?? o.extn?.term_year_labels ?? null),
      ),
      tenderId,
    );

    // Transform database offers to frontend Offer format
    const transformedOffers = (offers || []).map((dbOffer: any) => {
      // Get site_id from tender_site relationship or map
      const tenderSiteId = dbOffer.tender_site_id;
      const dbSiteId = dbOffer.tender_site?.site_id || tenderSiteToSiteIdMap.get(tenderSiteId);
      
      // Transform database structure to frontend Offer structure
      const frontendOffer: any = {
        id: dbOffer.id,
        orgId: dbOffer.org_id,
        tenderId: dbOffer.tender_id,
        tenderSiteId: tenderSiteId,
        siteId: dbSiteId, // Map to frontend site.id (will be matched with sites in context)
        siteName: dbOffer.tender_site?.name || null,
        retailerId: dbOffer.retailer_id || '',
        retailerName: dbOffer.retailer?.name || dbOffer.extn?.retailerName || 'Unknown Retailer',
        retailerLogo: dbOffer.retailer?.logo || dbOffer.extn?.retailerLogo || null,
        offerType: dbOffer.offer_type || 'electricity',
        greenPercent: dbOffer.green_percent || 0,
        feesJson: dbOffer.extn?.feesJson || {},
        sourceFileId: dbOffer.extn?.sourceFileId || null,
        sourceCitations: dbOffer.extn?.sourceCitations || null,
        confidence: dbOffer.extn?.confidence || 100,
        isRecommended: dbOffer.is_recommended || false,
        recommendedMonths: dbOffer.recommended_months || [],
        availableTerms: (() => {
          // PRECEDENCE, NOT UNION. These sources disagree, and the freshest one wins.
          //
          // extn.contract_terms is rewritten every time someone edits the terms in the
          // Extracted Data step; the available_terms COLUMN is not. Unioning them therefore
          // kept both the old and the new: a tender re-termed to 16/28/40/52 still carried
          // [12,24,36,48,60] in the column, and the union produced nine terms. Compare renders
          // one card per term, and the five with no cost data behind them rendered as empty
          // "blank offers" — one of which is selectable, and was selected.
          const parseTerms = (obj: unknown): number[] =>
            obj && typeof obj === 'object'
              ? Object.keys(obj as Record<string, unknown>)
                  .map(k => parseInt(k.split('_')[0], 10))
                  .filter(n => !isNaN(n) && n > 0)
              : [];

          const myr = dbOffer.multi_year_rate_data as Record<string, unknown> | null;
          const myrRoot = (myr?.smooth as Record<string, unknown> | undefined) || myr;
          const maxRateYear = myrRoot && typeof myrRoot === 'object'
            ? Object.keys(myrRoot)
                .filter(k => /^year_\d+$/.test(k))
                .map(k => parseInt(k.replace('year_', ''), 10))
                .reduce((m, n) => Math.max(m, n), 0)
            : 0;

          const fromContractTerms = parseTerms(dbOffer.extn?.contract_terms);
          if (fromContractTerms.length > 0) {
            const termSet = new Set<number>(fromContractTerms);
            // Legacy shape this expansion was written for: an old Shell/EA offer stored a
            // single "12" alongside year_1..year_4 in multi_year_rate_data, meaning it really
            // offered 12/24/36/48. Only that exact shape — one term, and it is 12 — is
            // expanded, so a genuine 16-month offer is never turned into 12/24/36/48/60.
            if (fromContractTerms.length === 1 && fromContractTerms[0] === 12 && maxRateYear > 1) {
              for (let i = 2; i <= maxRateYear; i++) termSet.add(i * 12);
            }
            return Array.from(termSet).sort((a, b) => a - b);
          }

          // No live contract_terms — fall back to the stored column, then to the rate-year
          // expansion, exactly as before.
          const termSet = new Set<number>((dbOffer.available_terms as number[] | null) || []);
          for (let i = 1; i <= maxRateYear; i++) termSet.add(i * 12);
          return termSet.size > 0 ? Array.from(termSet).sort((a, b) => a - b) : null;
        })(),
        expiryDate: dbOffer.expiry_date || null,
        validityDate: dbOffer.validity_date || null,
        multiYearRateData: dbOffer.multi_year_rate_data || null,
        rationale: dbOffer.extn?.rationale || null,
        estimatedAnnualCost: dbOffer.extn?.estimatedAnnualCost || null,
        estimatedSavings: dbOffer.extn?.estimatedSavings || null,
        estimatedSavingsPercent: dbOffer.extn?.estimatedSavingsPercent || null,
        offerLines: dbOffer.extn?.offerLines || [],
        createdAt: dbOffer.created_at ? new Date(dbOffer.created_at) : new Date(),
        updatedAt: dbOffer.updated_at ? new Date(dbOffer.updated_at) : new Date(),
        status: dbOffer.extn?.status || 'draft',
        notes: dbOffer.extn?.notes || null,
        extn: dbOffer.extn || {},
      };

      return frontendOffer;
    });

    console.log(`✅ FETCHED AND TRANSFORMED ${transformedOffers.length} OFFERS`);
    transformedOffers.forEach((offer, index) => {
      console.log(`   ${index + 1}. ${offer.id} - ${offer.retailerName} (siteId: ${offer.siteId || 'none'})`);
    });

    return { success: true, offers: transformedOffers };
  } catch (error) {
    console.error("❌ Unexpected error in getOffers:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error",
    };
  }
};

/**
 * Update tender workflow step progress - US-031
 * Calls the REST API endpoint created in US-029
 */
export const updateTenderStep = async (
  tenderId: string,
  currentStep: number,
  completedSteps: number[],
  stepData?: object
): Promise<{ success: boolean; error?: string }> => {
  try {
    const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

    const response = await fetch(`${API_URL}/api/scopingai/tenders/${tenderId}/step`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        currentStep,
        completedSteps,
        stepData
      })
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || `HTTP error ${response.status}`);
    }

    return { success: true };
  } catch (error: any) {
    console.error('❌ Error updating tender step:', error);
    return {
      success: false,
      error: error.message || 'Failed to update tender step'
    };
  }
};

/**
 * Save the user's ranked offer selections (1st/2nd/3rd choice) to the tender.
 * Stores as JSONB in tender.ranked_offer_selections.
 */
export const saveRankedOfferSelections = async (
  tenderId: string,
  selections: RankedOfferSelection[]
): Promise<{ success: boolean; error?: string }> => {
  try {
    const { error } = await supabase
      .from('tender')
      .update({ ranked_offer_selections: selections })
      .eq('id', tenderId);

    if (error) throw error;
    return { success: true };
  } catch (error: any) {
    console.error('❌ Error saving ranked offer selections:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Mirror the 1st Choice onto the offer rows, for the steps that read the legacy flag.
 *
 * ⚠️ TWO COLUMNS, BY ID. Deliberately NOT saveOffers: that rewrites every offer row and needs an
 * org, a client and a site map to do it, and when any of those was missing the mirror silently
 * did not happen at all — see recommendedFlagsForOffers for the tender it dead-ended.
 *
 * Idempotent and complete: the caller passes an entry for every offer, so deselecting retracts
 * the flag as surely as selecting sets it.
 */
export const saveRecommendedFlags = async (
  flags: Array<{ offerId: string; months: number[] }>,
): Promise<{ success: boolean; error?: string }> => {
  try {
    // Grouped so this is a handful of statements whatever the offer count, and so every row is
    // written even when several share a term.
    const byMonths = new Map<string, string[]>();
    flags.forEach(({ offerId, months }) => {
      const key = JSON.stringify(months);
      const ids = byMonths.get(key) ?? [];
      ids.push(offerId);
      byMonths.set(key, ids);
    });

    for (const [key, ids] of byMonths) {
      const months = JSON.parse(key) as number[];
      const { error } = await supabase
        .from('offer')
        .update({ is_recommended: months.length > 0, recommended_months: months })
        .in('id', ids);
      if (error) throw error;
    }
    return { success: true };
  } catch (error: any) {
    console.error('❌ Error saving recommended flags:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Load ranked offer selections from the tender.
 */
export const getRankedOfferSelections = async (
  tenderId: string
): Promise<{ success: boolean; selections?: RankedOfferSelection[]; error?: string }> => {
  try {
    const { data, error } = await supabase
      .from('tender')
      .select('ranked_offer_selections')
      .eq('id', tenderId)
      .single();

    if (error) throw error;
    return { success: true, selections: (data?.ranked_offer_selections as RankedOfferSelection[]) || [] };
  } catch (error: any) {
    console.error('❌ Error loading ranked offer selections:', error);
    return { success: false, error: error.message };
  }
};

